import React, { useState } from 'react';
import api from '../api/client';
import { Upload, FileText, Table, Download, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

const Import = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [result, setResult] = useState(null);

  const handlePdfUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', pdfFile);

    try {
      const response = await api.post('/api/import/pdf', formData, {
        params: { auto_translate: autoTranslate },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      toast.error('Please select an Excel file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', excelFile);

    try {
      const response = await api.post('/api/import/excel', formData, {
        params: { auto_translate: autoTranslate },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/import/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Import Vocabulary</h1>
        <p className="text-gray-600 mt-2">
          Upload PDFs or Excel files to extract and import Chinese vocabulary
        </p>
      </div>

      {/* Result Display */}
      {result && (
        <div className="mb-8 card bg-green-50 border border-green-200">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Import Complete!</h3>
              <p className="text-gray-700 mt-1">{result.message}</p>
              {result.deck_id && (
                <p className="text-sm text-gray-600 mt-2">
                  Deck ID: {result.deck_id}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="card mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">AI Translation</h3>
            <p className="text-sm text-gray-600">
              Automatically translate words to English and Vietnamese
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoTranslate}
              onChange={(e) => setAutoTranslate(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* PDF Upload */}
        <div className="card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-primary-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Upload PDF</h2>
            <p className="text-gray-600 mt-2">
              Extract Chinese words from PDF documents
            </p>
          </div>

          <form onSubmit={handlePdfUpload} className="space-y-4">
            <div>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setPdfFile(e.target.files[0])}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors duration-200"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {pdfFile ? pdfFile.name : 'Click to select PDF'}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={uploading || !pdfFile}
              className="w-full btn-primary"
            >
              {uploading ? 'Processing...' : 'Extract Words'}
            </button>
          </form>
        </div>

        {/* Excel Upload */}
        <div className="card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Table className="w-8 h-8 text-accent-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Upload Excel</h2>
            <p className="text-gray-600 mt-2">
              Import vocabulary from Excel spreadsheets
            </p>
          </div>

          <form onSubmit={handleExcelUpload} className="space-y-4">
            <div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setExcelFile(e.target.files[0])}
                className="hidden"
                id="excel-upload"
              />
              <label
                htmlFor="excel-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-accent-500 transition-colors duration-200"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {excelFile ? excelFile.name : 'Click to select Excel'}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={uploading || !excelFile}
              className="w-full btn-primary"
            >
              {uploading ? 'Processing...' : 'Import Words'}
            </button>
          </form>

          <button
            onClick={handleDownloadTemplate}
            className="w-full mt-4 btn-secondary flex items-center justify-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Download Template</span>
          </button>
        </div>
      </div>

      {/* Features */}
      <div className="card mt-8">
        <h3 className="font-bold text-gray-900 mb-4">Import Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">AI Translation</p>
              <p className="text-sm text-gray-600">
                Auto-translate to English and Vietnamese
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-accent-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Example Sentences</p>
              <p className="text-sm text-gray-600">
                Generate contextual example sentences
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Table className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Excel Export</p>
              <p className="text-sm text-gray-600">
                Export vocabulary for offline study
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Import;
